import { z } from "zod/v4";

import {
  AllNotificationTypeList,
  NotificationChannelTypeList,
  NotificationThresholdConfigDtoSchema,
  ThresholdEnabledNotificationTypeList,
} from "./notification-types";

// ── Constants ─────────────────────────────────────────────────────────────────

export const INTELLIGEMS_SLACK_TEAM_ID = "T017AQ7056Z";

/**
 * The Slack section of the app's Settings page. Every link to Slack settings
 * ends in this id, including the ones in our Slack messages, so the app and
 * the backends all read it from here.
 */
export const SLACK_SETTINGS_SECTION_ID = "slack-settings";

/** Where the app keeps its Slack settings. */
export const SLACK_SETTINGS_URL_PATH = `/settings#${SLACK_SETTINGS_SECTION_ID}`;

/**
 * Terminal Slack API auth errors. These mean an integration's token is
 * permanently unusable (workspace deactivated, token/app revoked, invalid
 * token) and the only remedy is reconnecting — as opposed to transient or
 * retryable failures. Mirrors the non-retryable auth codes Slack documents.
 */
export const TERMINAL_SLACK_AUTH_ERROR_CODES = [
  "account_inactive",
  "token_revoked",
  "invalid_auth",
] as const;

/**
 * Stable, machine-readable code returned in an error response body's
 * `data.code` when a Slack integration's token is terminally invalid. The
 * frontend keys its "reconnect Slack" UX off this value, decoupled from the
 * underlying Slack error string.
 */
export const SLACK_INTEGRATION_INACTIVE_ERROR_CODE =
  "SLACK_INTEGRATION_INACTIVE";

// ── Channel Binding ───────────────────────────────────────────────────────────

export const CreateSlackChannelBindingRequestSchema = z.object({
  teamId: z.string(),
  channelId: z.string(),
  channelName: z.string(),
  channelUrl: z.string().optional(),
  isDedicatedSupport: z.boolean().optional().default(false),
});

export type CreateSlackChannelBindingRequest = z.infer<
  typeof CreateSlackChannelBindingRequestSchema
>;

export const SlackOrgAccessDtoSchema = z.object({
  id: z.uuid(),
  channelBindingId: z.uuid(),
  organization: z.object({
    id: z.uuid(),
    name: z.string(),
  }),
  enabled: z.boolean(),
  aiEnabled: z.boolean(),
  slashEnabled: z.boolean(),
  createdAtTs: z.number(),
  lastUpdateTs: z.number(),
});

export type SlackOrgAccessDto = z.infer<typeof SlackOrgAccessDtoSchema>;

export const SlackChannelBindingDtoSchema = z.object({
  id: z.uuid(),
  integrationId: z.uuid(),
  teamId: z.string(),
  channelId: z.string(),
  channelName: z.string(),
  channelUrl: z.string().nullish(),
  isActive: z.boolean(),
  isDedicatedSupport: z.boolean(),
  aiAutoReplyEnabled: z.boolean(),
  welcomeMessageSentTs: z.number(),
  createdAtTs: z.number(),
  lastUpdateTs: z.number(),
  orgAccess: SlackOrgAccessDtoSchema.array(),
});

export type SlackChannelBindingDto = z.infer<
  typeof SlackChannelBindingDtoSchema
>;

// ── Org Access ────────────────────────────────────────────────────────────────

const SlackOrgAccessItemSchema = z.object({
  orgId: z.uuid(),
  enabled: z.boolean(),
  aiEnabled: z.boolean(),
  slashEnabled: z.boolean(),
});

export const BulkUpsertSlackOrgAccessRequestSchema = z.object({
  channelBindingId: z.uuid(),
  orgAccess: z.array(SlackOrgAccessItemSchema),
});

export type BulkUpsertSlackOrgAccessRequest = z.infer<
  typeof BulkUpsertSlackOrgAccessRequestSchema
>;

// ── Notification Routes ───────────────────────────────────────────────────────

const NotificationRouteItemSchema = z.object({
  orgId: z.uuid().nullable(),
  channelType: z.enum(NotificationChannelTypeList).default("slack"),
  notificationType: z.enum(AllNotificationTypeList),
  channelBindingId: z.uuid().nullable(),
  destination: z.record(z.string(), z.unknown()).nullable().optional(),
  enabled: z.boolean(),
  priority: z.number().int().default(0),
  routeConfig: z.record(z.string(), z.unknown()).nullable().optional(),
  sensitivityAcknowledged: z.boolean().default(false),
});

export const BulkUpsertNotificationRoutesRequestSchema = z.object({
  routes: z.array(NotificationRouteItemSchema),
});

export type BulkUpsertNotificationRoutesRequest = z.infer<
  typeof BulkUpsertNotificationRoutesRequestSchema
>;

export const NotificationRouteDtoSchema = z.object({
  id: z.uuid(),
  integrationId: z.uuid(),
  org: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  channelType: z.string(),
  notificationType: z.string(),
  channelBindingId: z.uuid().nullable(),
  destination: z.record(z.string(), z.unknown()).nullable(),
  enabled: z.boolean(),
  priority: z.number(),
  routeConfig: z.record(z.string(), z.unknown()).nullable(),
  sensitivityAcknowledged: z.boolean(),
  createdAtTs: z.number(),
  lastUpdateTs: z.number(),
});

export type NotificationRouteDto = z.infer<typeof NotificationRouteDtoSchema>;

export const CreateNotificationRouteDtoSchema = NotificationRouteDtoSchema.omit(
  {
    createdAtTs: true,
    lastUpdateTs: true,
    id: true,
    org: true,
  }
).extend({
  orgId: z.uuid().nullable(),
});

export type CreateNotificationRouteDto = z.infer<
  typeof CreateNotificationRouteDtoSchema
>;

// ── Transactional Config (PUT /v2/slack/config) ───────────────────────────────

export const PutSlackConfigRequestSchema = z.object({
  lastUpdateTs: z.number(),
  notificationsEnabled: z.boolean().optional(),
  deleteChannelBindingIds: z.array(z.uuid()).optional(),
  channels: z
    .array(
      z.object({
        id: z.uuid().optional(),
        teamId: z.string(),
        channelId: z.string(),
        channelName: z.string(),
        channelUrl: z.string().optional(),
        isDedicatedSupport: z.boolean().optional().default(false),
        isActive: z.boolean().optional().default(true),
        aiAutoReplyEnabled: z.boolean().optional(),
        createDefaultRoutesForOrgId: z.uuid().optional(),
      })
    )
    .optional(),
  orgAccess: z
    .array(
      z.object({
        channelBindingId: z.uuid(),
        orgId: z.uuid(),
        enabled: z.boolean(),
        aiEnabled: z.boolean(),
        slashEnabled: z.boolean(),
      })
    )
    .optional(),
  notificationRoutes: z
    .array(
      z.object({
        id: z.uuid().optional(),
        orgId: z.uuid().nullable(),
        channelType: z.enum(NotificationChannelTypeList).default("slack"),
        notificationType: z.enum(AllNotificationTypeList),
        channelBindingId: z.uuid().nullable(),
        destination: z.record(z.string(), z.unknown()).nullable().optional(),
        enabled: z.boolean(),
        priority: z.number().int().default(0),
        routeConfig: z.record(z.string(), z.unknown()).nullable().optional(),
        sensitivityAcknowledged: z.boolean().default(false),
      })
    )
    .optional(),
  thresholdConfigs: z
    .array(
      z.object({
        orgId: z.uuid().nullable(),
        notificationType: z.enum(ThresholdEnabledNotificationTypeList),
        thresholds: z.record(z.string(), z.unknown()),
      })
    )
    .optional(),
  deleteThresholdConfigs: z
    .array(
      z.object({
        orgId: z.uuid().nullable(),
        notificationType: z.enum(ThresholdEnabledNotificationTypeList),
      })
    )
    .optional(),
});

export type PutSlackConfigRequest = z.infer<typeof PutSlackConfigRequestSchema>;

// ── Config Response (GET /slack/config) ───────────────────────────────────────

export const SlackConfigDtoSchema = z.object({
  integrationId: z.uuid(),
  enabled: z.boolean(),
  organizationId: z.uuid().nullish(),
  agencyId: z.uuid().nullish(),
  notificationsEnabled: z.boolean().optional().default(false),
  channels: z.array(
    SlackChannelBindingDtoSchema.extend({
      orgAccess: z.array(SlackOrgAccessDtoSchema),
    })
  ),
  notificationRoutes: z.array(NotificationRouteDtoSchema),
  thresholdConfigs: z
    .array(NotificationThresholdConfigDtoSchema)
    .optional()
    .default([]),
  lastUpdateTs: z.number(),
  /** A workspace is connected through admin OAuth right now. */
  isAdmin: z.boolean(),
  isMissingScopes: z.boolean(),
});

export type SlackConfigDto = z.infer<typeof SlackConfigDtoSchema>;

// ── AI App Cache Type ─────────────────────────────────────────────────────────

const SlackChannelOrgAccessConfigSchema = z.object({
  orgId: z.uuid(),
  orgName: z.string(),
  shopId: z.string(),
  enabled: z.boolean(),
  aiEnabled: z.boolean(),
  slashEnabled: z.boolean(),
});

export const SlackChannelAccessConfigSchema = z.object({
  teamId: z.string(),
  channelId: z.string(),
  channelName: z.string(),
  integrationId: z.uuid(),
  isActive: z.boolean().optional(),
  isDedicatedSupport: z.boolean(),
  aiAutoReplyEnabled: z.boolean().optional(),
  welcomeMessageSentTs: z.number().optional(),
  isAdmin: z.boolean().optional(),
  accessToken: z.string().optional(),
  missingScopes: z.array(z.string()).optional(),
  orgAccess: z.array(SlackChannelOrgAccessConfigSchema),
});

export type SlackChannelAccessConfig = z.infer<
  typeof SlackChannelAccessConfigSchema
>;

// ── Internal Mappings (GET /internal/slack-channel-mappings) ──────────────────

export const GetSlackChannelMappingsResponseBodySchema = z.object({
  mappings: z.array(SlackChannelAccessConfigSchema),
});

export type GetSlackChannelMappingsResponseBodyType = z.infer<
  typeof GetSlackChannelMappingsResponseBodySchema
>;
